import { useState, useEffect, useCallback } from "react";
import {
	CiStar,
	CiUser,
	CiCalendar,
	CiTrophy,
	CiCircleCheck,
	CiClock1,
} from "react-icons/ci";
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend,
	ArcElement,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import PageTitle from "../../components/common/PageTitle";
import FullPageLoader from "../../components/ui/FullPageLoader";
import QRCodeModal from "../../components/common/QRCodeModal";
import { API_URL } from "../../utilities/constants";
import { useUI } from "../../context/ui";
import { useFetch } from "../../hooks/useFetch";
import { formatAmountValue, formatDateTime } from "../../utilities/helpers";

ChartJS.register(
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend,
	ArcElement
);

// ====== Helpers ====== //

const formatYYYYMM = (yyyymm) => {
	if (!yyyymm || String(yyyymm).length !== 6) return yyyymm;
	const s = String(yyyymm);
	const y = s.substring(0, 4);
	const m = s.substring(4, 6);
	const months = [
		"Jan", "Feb", "Mar", "Apr", "May", "Jun",
		"Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
	];
	return `${months[parseInt(m) - 1]} ${y}`;
};

const CommissionStatusBadge = ({ status }) => {
	const map = {
		PENDING: { bg: "#fef3c7", color: "#b45309" },
		APPROVED: { bg: "#dbeafe", color: "#1d4ed8" },
		PAID: { bg: "#d1fae5", color: "#059669" },
	};
	const style = map[status] || { bg: "#f3f4f6", color: "#6b7280" };
	return (
		<span
			style={{
				display: "inline-block",
				padding: "2px 10px",
				borderRadius: "20px",
				fontSize: "11px",
				fontWeight: 600,
				background: style.bg,
				color: style.color,
				textTransform: "uppercase",
				letterSpacing: "0.3px",
			}}>
			{status}
		</span>
	);
};

// ====== Agent Referral Page ====== //

const AgentReferral = () => {
	const { setSegmentTitle } = useUI();

	const [loading, setLoading] = useState(false);
	const [perfData, setPerfData] = useState(null);
	const [filterMonth, setFilterMonth] = useState(() => {
		const d = new Date();
		return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}`;
	});

	// Referred customers filter & pagination
	const [referralFilter, setReferralFilter] = useState("all"); // "all" | "pending" | "converted"
	const [referralPage, setReferralPage] = useState(0);

	// ---- Referral code ----
	const { data: referralCodeData, loading: codeLoading } = useFetch(
		API_URL + "/api/1.0.0/referral/me/code",
		{ method: "GET" }
	);
	const referralCode =
		referralCodeData?.data?.referralCode ||
		referralCodeData?.data?.code ||
		null;

	// ---- Referred customers URL ----
	const referralsUrl = (() => {
		const params = new URLSearchParams({
			page: String(referralPage),
			size: "20",
		});
		if (referralFilter === "pending") params.set("firstTxnDone", "false");
		if (referralFilter === "converted") params.set("firstTxnDone", "true");
		return `${API_URL}/api/1.0.0/agent/referral/referrals?${params.toString()}`;
	})();

	const { data: referralsData, loading: referralsLoading } = useFetch(
		referralsUrl,
		{ method: "GET" }
	);
	const referrals = referralsData?.data?.items || [];
	const referralsTotal = referralsData?.data?.total || 0;
	const referralsTotalPages = referralsData?.data?.totalPages || 0;

	const handleFilterChange = (newFilter) => {
		setReferralFilter(newFilter);
		setReferralPage(0);
	};

	useEffect(() => {
		setSegmentTitle("My Referrals");
	}, []);

	// ---- Fetch performance data (manual — re-fetches on month change) ----
	const fetchPerformance = useCallback(async () => {
		setLoading(true);
		try {
			const resp = await fetch(
				`${API_URL}/api/1.0.0/admin/referral/agent-performance?periodMonth=${filterMonth}`,
				{
					method: "GET",
					headers: {
						"Content-Type": "application/json",
						"X-JWT-Assertion": localStorage.getItem("token"),
					},
				}
			);
			if (resp.ok) {
				const json = await resp.json();
				setPerfData(json.data || null);
			}
		} catch (e) {
			console.error("Error fetching performance", e);
		} finally {
			setLoading(false);
		}
	}, [filterMonth]);

	useEffect(() => {
		fetchPerformance();
	}, [fetchPerformance]);

	// For role 4 (petrol attendant), agents[0] is their own record
	const agent = perfData?.agents?.[0] || null;
	const onboardingCount = agent?.onboardingCount || 0;
	const firstTxnCount = agent?.firstTxnCount || 0;
	const conversionRate = agent?.conversionRate || 0;
	const totalCommissionCents = agent?.totalCommissionCents || 0;
	const pendingCents = agent?.pendingCents || 0;
	const approvedCents = agent?.approvedCents || 0;
	const paidCents = agent?.paidCents || 0;

	// "YYYYMM" → "YYYY-MM" for the month input value
	const monthInputValue =
		filterMonth.length === 6
			? `${filterMonth.substring(0, 4)}-${filterMonth.substring(4, 6)}`
			: "";

	// ---- Bar chart: Onboardings vs First Txns ----
	const barChartData = {
		labels: ["This Period"],
		datasets: [
			{
				label: "Onboardings",
				data: [onboardingCount],
				backgroundColor: "#3b82f6",
				borderRadius: 5,
				borderSkipped: false,
			},
			{
				label: "First Txns",
				data: [firstTxnCount],
				backgroundColor: "#10b981",
				borderRadius: 5,
				borderSkipped: false,
			},
		],
	};
	const barChartOptions = {
		responsive: true,
		plugins: {
			legend: { position: "top" },
			title: { display: false },
		},
		scales: {
			y: { beginAtZero: true, ticks: { precision: 0 } },
		},
	};

	// ---- Doughnut chart: commission breakdown ----
	const doughnutData = {
		labels: ["Pending", "Approved", "Paid"],
		datasets: [
			{
				data: [pendingCents, approvedCents, paidCents],
				backgroundColor: ["#f59e0b", "#3b82f6", "#10b981"],
				borderWidth: 2,
				borderColor: "#fff",
			},
		],
	};
	const doughnutOptions = {
		responsive: true,
		cutout: "65%",
		plugins: {
			legend: { position: "bottom" },
			tooltip: {
				callbacks: {
					label: (ctx) =>
						` ${ctx.label}: ${formatAmountValue(ctx.raw / 100)}`,
				},
			},
		},
	};

	const FILTER_TABS = [
		{ key: "all", label: "All" },
		{ key: "pending", label: "Not Converted" },
		{ key: "converted", label: "Converted" },
	];

	return (
		<section>
			<PageTitle title='My Referrals' />
			<FullPageLoader isActive={loading || codeLoading} />

			{/* ---- Referral Code Hero ---- */}
			<div className='agent-ref-hero'>
				<div className='agent-ref-hero__inner'>
					<div className='agent-ref-hero__icon'>
						<CiStar />
					</div>
					<div className='agent-ref-hero__code-block'>
						<p className='agent-ref-hero__label'>Your Referral Code</p>
						{codeLoading ? (
							<p className='agent-ref-hero__code'>Loading...</p>
						) : referralCode ? (
							<div className='agent-ref-hero__code-row'>
								<p className='agent-ref-hero__code'>{referralCode}</p>
								<QRCodeModal referralCode={referralCode} />
							</div>
						) : (
							<p className='agent-ref-hero__code agent-ref-hero__code--empty'>
								Not assigned
							</p>
						)}
					</div>
				</div>
			</div>

			{/* ---- Month Filter ---- */}
			<div className='referral-filter-bar'>
				<div className='filter-group'>
					<label>Period</label>
					<input
						type='month'
						value={monthInputValue}
						onChange={(e) => {
							const v = e.target.value.replace("-", "");
							if (v.length === 6) setFilterMonth(v);
						}}
					/>
				</div>
				<button
					className='light'
					onClick={fetchPerformance}
					disabled={loading}>
					<span>
						<CiCalendar />
						Apply
					</span>
				</button>
			</div>

			{/* ---- Period Label Bar ---- */}
			<div className='commission-summary-bar'>
				<span>
					Performance for{" "}
					<strong>{formatYYYYMM(filterMonth)}</strong>
				</span>
			</div>

			{/* ---- Empty State ---- */}
			{!loading && !agent && (
				<div
					style={{
						padding: "60px 20px",
						textAlign: "center",
						color: "#8f8f8f",
					}}>
					<CiTrophy size={48} style={{ opacity: 0.3 }} />
					<p style={{ marginTop: "12px", fontSize: "14px" }}>
						No performance data available for this period.
					</p>
				</div>
			)}

			{agent && (
				<>
					{/* ---- KPI Cards ---- */}
					<div className='referral-kpi-grid'>
						<div className='referral-kpi-card'>
							<div
								className='kpi-icon'
								style={{ background: "#eff6ff" }}>
								<CiUser style={{ color: "#3b82f6" }} />
							</div>
							<div className='kpi-content'>
								<p className='kpi-label'>Onboardings</p>
								<p className='kpi-value'>{onboardingCount}</p>
							</div>
						</div>
						<div className='referral-kpi-card'>
							<div
								className='kpi-icon'
								style={{ background: "#f0fdf4" }}>
								<CiTrophy style={{ color: "#10b981" }} />
							</div>
							<div className='kpi-content'>
								<p className='kpi-label'>First Txns</p>
								<p className='kpi-value'>{firstTxnCount}</p>
							</div>
						</div>
						<div className='referral-kpi-card'>
							<div
								className='kpi-icon'
								style={{ background: "#fef3c7" }}>
								<CiStar style={{ color: "#f59e0b" }} />
							</div>
							<div className='kpi-content'>
								<p className='kpi-label'>Conversion Rate</p>
								<p className='kpi-value'>
									{conversionRate.toFixed(1)}%
								</p>
							</div>
						</div>
						<div className='referral-kpi-card'>
							<div
								className='kpi-icon'
								style={{ background: "#f0fdf4" }}>
								<CiStar style={{ color: "#10b981" }} />
							</div>
							<div className='kpi-content'>
								<p className='kpi-label'>Total Earned</p>
								<p className='kpi-value'>
									{formatAmountValue(totalCommissionCents / 100)}
								</p>
							</div>
						</div>
					</div>

					{/* ---- Charts Row ---- */}
					<div className='referral-charts-row'>
						<div className='referral-chart-box'>
							<h6>Onboardings vs First Transactions</h6>
							<br />
							<Bar
								data={barChartData}
								options={barChartOptions}
								height={110}
							/>
						</div>
						<div className='referral-chart-box referral-chart-box--sm'>
							<h6>Commission Breakdown</h6>
							<br />
							{pendingCents + approvedCents + paidCents > 0 ? (
								<div style={{ position: "relative" }}>
									<Doughnut
										data={doughnutData}
										options={doughnutOptions}
									/>
									<div className='agent-perf-donut-total'>
										<p
											style={{
												fontSize: "11px",
												color: "#8f8f8f",
											}}>
											Total
										</p>
										<p
											style={{
												fontWeight: 700,
												fontSize: "14px",
											}}>
											{formatAmountValue(
												totalCommissionCents / 100
											)}
										</p>
									</div>
								</div>
							) : (
								<p
									style={{
										color: "#8f8f8f",
										fontSize: "13px",
										textAlign: "center",
										paddingTop: "20px",
									}}>
									No commission data
								</p>
							)}
						</div>
					</div>

					{/* ---- Commission Summary Table ---- */}
					<div style={{ padding: "0 20px 40px 20px" }}>
						<div className='referral-explorer-result'>
							<h6
								style={{
									fontSize: "14px",
									fontWeight: 600,
									marginBottom: "16px",
								}}>
								Commission Summary
							</h6>
							<table
								className='rs-table'
								style={{ width: "100%" }}>
								<thead>
									<tr>
										<th>Status</th>
										<th style={{ textAlign: "right" }}>
											Amount
										</th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td>
											<span style={{ color: "#f59e0b", fontWeight: 600 }}>
												● Pending
											</span>
										</td>
										<td style={{ textAlign: "right" }}>
											{formatAmountValue(pendingCents / 100)}
										</td>
									</tr>
									<tr>
										<td>
											<span style={{ color: "#3b82f6", fontWeight: 600 }}>
												● Approved
											</span>
										</td>
										<td style={{ textAlign: "right" }}>
											{formatAmountValue(approvedCents / 100)}
										</td>
									</tr>
									<tr>
										<td>
											<span style={{ color: "#10b981", fontWeight: 600 }}>
												● Paid
											</span>
										</td>
										<td style={{ textAlign: "right" }}>
											{formatAmountValue(paidCents / 100)}
										</td>
									</tr>
								</tbody>
								<tfoot>
									<tr className='agent-perf-table-total'>
										<td>
											<strong>Total</strong>
										</td>
										<td style={{ textAlign: "right" }}>
											<strong>
												{formatAmountValue(
													totalCommissionCents / 100
												)}
											</strong>
										</td>
									</tr>
								</tfoot>
							</table>
						</div>
					</div>
				</>
			)}

			{/* ---- Referred Customers ---- */}
			<div className='agent-ref-customers'>
				<FullPageLoader isActive={referralsLoading} />

				<div className='agent-ref-customers__header'>
					<div>
						<h6 className='agent-ref-customers__title'>
							My Referred Customers
						</h6>
						<p className='agent-ref-customers__subtitle'>
							{referralsTotal > 0
								? `${referralsTotal} customer${referralsTotal !== 1 ? "s" : ""} referred in total`
								: "Customers you have onboarded via your referral code"}
						</p>
					</div>

					{/* Filter tabs */}
					<div className='agent-ref-customers__tabs'>
						{FILTER_TABS.map((tab) => (
							<button
								key={tab.key}
								className={referralFilter === tab.key ? "agent-ref-tab agent-ref-tab--active" : "agent-ref-tab"}
								onClick={() => handleFilterChange(tab.key)}>
								{tab.label}
							</button>
						))}
					</div>
				</div>

				{/* Empty state */}
				{!referralsLoading && referrals.length === 0 && (
					<div className='agent-ref-customers__empty'>
						<CiUser size={40} style={{ opacity: 0.25 }} />
						<p>
							{referralFilter === "pending"
								? "All your referred customers have already converted."
								: referralFilter === "converted"
								? "None of your referred customers have converted yet."
								: "You haven't referred any customers yet."}
						</p>
					</div>
				)}

				{/* Table */}
				{referrals.length > 0 && (
					<>
						<div className='rs-table-holder' style={{ padding: "0" }}>
							<table className='rs-table'>
								<thead>
									<tr>
										<th>#</th>
										<th>Customer</th>
										<th>Registered</th>
										<th>First Txn</th>
										<th>Commission</th>
										<th>Commission Status</th>
									</tr>
								</thead>
								<tbody>
									{referrals.map((r, i) => (
										<tr key={r.referredUserId || i}>
											<td style={{ color: "#8f8f8f", fontSize: "12px" }}>
												{referralPage * 20 + i + 1}
											</td>
											<td>
												<strong style={{ fontSize: "13px", display: "block" }}>
													{r.referredUserName || "—"}
												</strong>
												<span style={{ fontSize: "11px", color: "#8f8f8f" }}>
													{r.referredUserPhone || "—"}
												</span>
											</td>
											<td style={{ fontSize: "13px" }}>
												{r.registeredAt
													? formatDateTime(r.registeredAt)
													: "—"}
											</td>
											<td>
												{r.firstTxnDone ? (
													<div>
														<div
															style={{
																display: "flex",
																alignItems: "center",
																gap: "4px",
																color: "#059669",
																fontSize: "12px",
																fontWeight: 600,
															}}>
															<CiCircleCheck size={14} />
															Done
														</div>
														{r.firstTxnAt && (
															<span style={{ fontSize: "11px", color: "#8f8f8f" }}>
																{formatDateTime(r.firstTxnAt)}
															</span>
														)}
													</div>
												) : (
													<div
														style={{
															display: "flex",
															alignItems: "center",
															gap: "4px",
															color: "#f59e0b",
															fontSize: "12px",
															fontWeight: 600,
														}}>
														<CiClock1 size={14} />
														Pending
													</div>
												)}
											</td>
											<td style={{ fontSize: "13px" }}>
												{r.commissionCents != null
													? formatAmountValue(r.commissionCents / 100)
													: "—"}
											</td>
											<td>
												{r.commissionStatus ? (
													<CommissionStatusBadge
														status={r.commissionStatus}
													/>
												) : (
													<span style={{ color: "#8f8f8f", fontSize: "12px" }}>—</span>
												)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>

						{/* Pagination */}
						{referralsTotalPages > 1 && (
							<div className='agent-ref-customers__pagination'>
								<button
									className='light'
									disabled={referralPage === 0}
									onClick={() => setReferralPage((p) => p - 1)}>
									<span>← Prev</span>
								</button>
								<span className='agent-ref-customers__page-info'>
									Page {referralPage + 1} of {referralsTotalPages}
								</span>
								<button
									className='light'
									disabled={referralPage >= referralsTotalPages - 1}
									onClick={() => setReferralPage((p) => p + 1)}>
									<span>Next →</span>
								</button>
							</div>
						)}
					</>
				)}
			</div>
		</section>
	);
};

export default AgentReferral;
